import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Badge,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, MessageSquare } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { TicketRequestForm } from '@/components/forms/TicketRequestForm'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { format, parseISO } from 'date-fns'
import { CsatScoreDisplay } from '@/components/csat/CsatScoreDisplay'
import apiClient from '@/lib/api/client'

const basePath = 'support/my-request'

interface TicketDetail {
  id: number
  request_number?: string
  service_type_name?: string
  description?: string
  location?: string | null
  ticket_status_label?: string
  created_at?: string
  last_updated_at_human?: string
  csat_score?: string | null
  csat_score_label?: string | null
  csat_submitted_at?: string | null
  [key: string]: unknown
}

interface TicketUpdateItem {
  id: number
  content: string
  type?: string
  created_at?: string
  author?: { full_name?: string }
}

export default function MyRequestsPage() {
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const colors = useThemeColors()
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRequests, setTotalRequests] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [activeTab, setActiveTab] = useState<'all' | 'trash'>('all')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const [viewDetail, setViewDetail] = useState<TicketDetail | null>(null)
  const [viewUpdates, setViewUpdates] = useState<TicketUpdateItem[]>([])
  const [viewLoading, setViewLoading] = useState(false)
  const location = useLocation()

  const access = useAccessCheck('/my-request')

  // When navigated from /my-request/:id, open edit modal once for that id
  const hasOpenedFromStateRef = useRef(false)
  useEffect(() => {
    const state = location.state as { editId?: number } | null
    if (state?.editId && !hasOpenedFromStateRef.current) {
      hasOpenedFromStateRef.current = true
      setEditingId(state.editId)
      onOpen()
    }
  }, [location.state, onOpen])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRequests(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const q = searchRef.current?.value ?? ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const openViewModal = useCallback((id: number) => {
    setViewId(id)
    setViewDetail(null)
    setViewUpdates([])
    onViewOpen()
  }, [onViewOpen])

  useEffect(() => {
    if (!viewId || !isViewOpen) return
    setViewLoading(true)
    const path = basePath
    Promise.all([
      apiClient.get<{ data: TicketDetail }>(`${path}/${viewId}`),
      apiClient.get<{ data: TicketUpdateItem[] }>(`${path}/${viewId}/updates`),
    ])
      .then(([detailRes, updatesRes]) => {
        setViewDetail(detailRes.data.data ?? null)
        const payload = updatesRes.data as { data?: TicketUpdateItem[] }
        setViewUpdates(Array.isArray(payload?.data) ? payload.data : [])
      })
      .catch(() => {
        setViewDetail(null)
        setViewUpdates([])
      })
      .finally(() => setViewLoading(false))
  }, [viewId, isViewOpen])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      selectable: false,
      dataFields: {
        request_number: { name: 'Request #', withSort: true, maxWidth: '160px' },
        service_type_name: { name: 'Service', maxWidth: '160px' },
        category_name: { name: 'Category', withSort: false, maxWidth: '140px' },
        subcategory_name: { name: 'Sub-category', withSort: false, maxWidth: '140px' },
        item_name: { name: 'Item', withSort: false, maxWidth: '140px' },
        location: { name: 'Location', withSort: false, maxWidth: '180px' },
        description: { name: 'Issue', maxWidth: '340px' },
        ticket_status_label: {
          name: 'Status',
          withFilter: true,
          maxWidth: '140px',
          badge: {
            New: 'blue',
            Pending: 'yellow',
            'In Progress': 'orange',
            Resolved: 'green',
            Closed: 'gray',
            Cancelled: 'red',
            Draft: 'gray',
            Assigned: 'purple',
            'For Approval': 'orange',
            Approved: 'green',
            Rejected: 'red',
          },
        },
        created_at: { name: 'Date Created', withSort: true, maxWidth: '180px' },
        last_updated_at_human: { name: 'Last Updated', maxWidth: '120px' },
      },
      dataSource: activeTab === 'trash' ? `${basePath}/archived` : basePath,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: true,
      noRecordText: 'No requests found',
      otherActions: [
        {
          name: 'View updates',
          onClick: (row: { id: number }) => openViewModal(row.id),
        },
      ],
    }),
    [activeTab, openViewModal]
  )

  const customRenderers = useMemo(
    () => ({
      created_at: (value: string | null | undefined) => {
        if (!value) return ''
        try {
          const date = typeof value === 'string' ? parseISO(value) : new Date(value)
          return format(date, 'MMM d, yyyy · h:mm a')
        } catch {
          return String(value)
        }
      },
    }),
    []
  )

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                My Requests
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                View and manage your support requests
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All <Badge ml={2} colorScheme="blue">{totalRequests}</Badge>
                      </Tab>
                      <Tab>
                        Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                      </Tab>
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      createLabel="Create Request"
                      onCreate={() => {
                        setEditingId(null)
                        onOpen()
                      }}
                      pathname="/my-request"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request number, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  defaultSort={{ order: 'created_at', sort: 'desc' }}
                  customRenderers={customRenderers}
                  access={{
                    can_create: access.can_create,
                    can_edit: access.can_edit,
                    can_delete: access.can_delete,
                    can_view: access.can_view,
                    can_export: access.can_export,
                    can_import: access.can_import,
                    canEditRow: (row: { ticket_status_label?: string }) => row.ticket_status_label === 'New',
                    canDeleteRow: (row: { ticket_status_label?: string }) => row.ticket_status_label === 'New',
                  }}
                  onOpenModal={(id: number) => {
                    setEditingId(id)
                    onOpen()
                  }}
                  setSubSub={showSubSubCallback}
                  params={tableParams}
                  onRowClick={(row: { id: number }) => navigate(`/my-request/${row.id}`)}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setEditingId(null)
        }}
        size="2xl"
        isCentered
        blockScrollOnMount
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" display="flex" flexDirection="column">
          <ModalHeader py={3}>
            <Text fontSize="md" fontWeight="700">
              {editingId != null ? 'Edit Request' : 'Create Request'}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" flex="1" minH={0}>
            <TicketRequestForm
              key={editingId ?? 'new'}
              ticketRequestId={editingId ?? undefined}
              basePath={basePath}
              requestorMode
              onSuccess={() => {
                tableRef.current?.reload()
                onClose()
                setEditingId(null)
              }}
              onCancel={() => {
                onClose()
                setEditingId(null)
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => {
          onViewClose()
          setViewId(null)
          setViewDetail(null)
          setViewUpdates([])
        }}
        size="2xl"
        isCentered
        blockScrollOnMount
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" display="flex" flexDirection="column">
          <ModalHeader py={3}>
            <Text fontSize="md" fontWeight="700">
              Request details & updates
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" flex="1" minH={0}>
            {viewLoading ? (
              <HStack justify="center" py={8}>
                <Spinner size="lg" />
              </HStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                {viewDetail && (
                  <Box>
                    <Heading size="sm" mb={2}>
                      Details
                    </Heading>
                    <VStack align="stretch" spacing={1} fontSize="sm">
                      {viewDetail.request_number && (
                        <HStack>
                          <Text fontWeight="600" color={colors.textMuted}>Request #:</Text>
                          <Text>{viewDetail.request_number}</Text>
                        </HStack>
                      )}
                      {viewDetail.service_type_name != null && (
                        <HStack>
                          <Text fontWeight="600" color={colors.textMuted}>Service:</Text>
                          <Text>{viewDetail.service_type_name}</Text>
                        </HStack>
                      )}
                      {viewDetail.ticket_status_label != null && (
                        <HStack>
                          <Text fontWeight="600" color={colors.textMuted}>Status:</Text>
                          <Badge colorScheme="blue">{viewDetail.ticket_status_label}</Badge>
                        </HStack>
                      )}
                      <Box pt={1}>
                        <CsatScoreDisplay
                          score={viewDetail.csat_score}
                          scoreLabel={viewDetail.csat_score_label}
                          submittedAt={viewDetail.csat_submitted_at}
                          compact
                        />
                      </Box>
                      {viewDetail.location != null && String(viewDetail.location).trim() !== '' && (
                        <HStack align="flex-start">
                          <Text fontWeight="600" color={colors.textMuted} flexShrink={0}>Location:</Text>
                          <Text>{String(viewDetail.location)}</Text>
                        </HStack>
                      )}
                      {viewDetail.created_at && (
                        <HStack>
                          <Text fontWeight="600" color={colors.textMuted}>Created:</Text>
                          <Text>
                            {format(parseISO(viewDetail.created_at), 'MMM d, yyyy · h:mm a')}
                          </Text>
                        </HStack>
                      )}
                      {viewDetail.description != null && (
                        <Box pt={1}>
                          <Text fontWeight="600" color={colors.textMuted} mb={1}>Issue</Text>
                          <Text>{viewDetail.description}</Text>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}
                {viewDetail && viewUpdates.length > 0 && <Divider />}
                <Box>
                  <Heading size="sm" mb={2}>
                    Ticket updates
                  </Heading>
                  {viewUpdates.length === 0 && !viewLoading && (
                    <Text fontSize="sm" color={colors.textMuted}>No updates yet.</Text>
                  )}
                  <List spacing={3}>
                    {viewUpdates.map((u) => (
                      <ListItem key={u.id} display="flex" alignItems="flex-start" gap={2}>
                        <ListIcon as={MessageSquare} mt={0.5} color={colors.primary} />
                        <Box flex="1">
                          <Text fontSize="sm" whiteSpace="pre-wrap">{u.content}</Text>
                          <HStack mt={1} fontSize="xs" color={colors.textMuted}>
                            {u.author?.full_name && <Text>{u.author.full_name}</Text>}
                            {u.created_at && (
                              <Text>
                                {format(parseISO(u.created_at), 'MMM d, yyyy · h:mm a')}
                              </Text>
                            )}
                          </HStack>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
